/**
 * お肌管理ページに掲載している各施術の「個別施術詳細ページ」用データ。
 * NOTE: 正式な施術内容・成分・機器・効果・ダウンタイム・副作用・料金は未確定のため、
 *       ここでは推測した医療情報を作成しない。各セクションは「後から入力できるプレースホルダー」として扱う。
 *       お悩みは、既存カテゴリページに掲載済みの事実情報のみを使用する。
 *       FV画像は文字なしのビジュアル専用。番号・カテゴリ・施術名は HTML 側で表示する。
 *
 *       施術体系：
 *         ヴィーナス シリーズ＝プラズマ方式 / シンデレラ シリーズ＝ダーマペン4方式
 *         ステムサップ＝StemSup（幹細胞培養上清液） / ダイヤモンド＝リジュラン系
 */

export type SkinConcernTag = {
  label: string;
  target: string;
};

/** お肌管理の施術方式（4分類） */
export type SkinTreatmentCategory = "dermapen" | "peeling" | "plasma" | "photo-facial";

export type SkinTreatment = {
  slug: string;
  /** 施術番号（01〜10）。FVの左カラムで表示する。 */
  no: string;
  en: string;
  jp: string;
  /** 施術方式（4分類）。お肌管理親ページ・料金表の分類に使用する。 */
  category: SkinTreatmentCategory;
  /** FV画像（文字なしのビジュアル専用）。ページごとに1対1で差し替え可能 */
  heroImage?: string;
  /** FV画像の object-position（人物の見え方をページごとに調整） */
  heroObjectPosition?: string;
  heroImageAlt: string;
  heroNote: string;
  /** 確定している基本情報の補足（任意。未確定の推測情報は追加しない） */
  aboutNote?: string;
  /** お肌管理ページで本施術をご案内しているお悩み（既存データに基づく事実情報） */
  concerns: SkinConcernTag[];
};

const ACNE: SkinConcernTag = { label: "ニキビ", target: "skin-acne" };
const PIGMENT: SkinConcernTag = { label: "シミ・くすみ", target: "skin-pigment" };
const PORES: SkinConcernTag = { label: "毛穴", target: "skin-pores" };
const LIFTING: SkinConcernTag = { label: "たるみ・シワ", target: "skin-lifting" };

export const skinTreatments: SkinTreatment[] = [
  /* 01〜10：施術番号順に掲載（分類は各データの category を参照） */
  {
    slug: "venus-stemsup",
    no: "01",
    en: "VENUS STEMSUP",
    jp: "ヴィーナス ステムサップ",
    category: "plasma",
    heroImage:
      "/images/site/skin-venus-stemsup-visual-v1.webp",
    heroImageAlt: "ヴィーナス ステムサップの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, PORES, LIFTING],
  },
  {
    slug: "venus-diamond",
    no: "02",
    en: "VENUS DIAMOND",
    jp: "ヴィーナス ダイヤモンド",
    category: "plasma",
    heroImage:
      "/images/site/skin-venus-diamond-visual-v1.webp",
    heroImageAlt: "ヴィーナス ダイヤモンドの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, PORES, LIFTING],
  },
  {
    slug: "cinderella-stemsup",
    no: "03",
    en: "CINDERELLA STEMSUP",
    jp: "シンデレラ ステムサップ",
    category: "dermapen",
    heroImage:
      "/images/site/skin-cinderella-stemsup-visual-v1.webp",
    heroImageAlt: "シンデレラ ステムサップの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, PORES, LIFTING],
  },
  {
    slug: "cinderella-diamond",
    no: "04",
    en: "CINDERELLA DIAMOND",
    jp: "シンデレラ ダイヤモンド",
    category: "dermapen",
    heroImage:
      "/images/site/skin-cinderella-diamond-visual-v1.webp",
    heroImageAlt: "シンデレラ ダイヤモンドの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, PORES, LIFTING],
  },
  {
    slug: "velvet-skin",
    no: "05",
    en: "VELVET SKIN",
    jp: "ヴェルヴェットスキン",
    category: "dermapen",
    heroImage:
      "/images/site/skin-velvet-skin-visual-v6.webp",
    heroImageAlt: "ヴェルヴェットスキンの施術イメージ",
    heroNote: "施術画像は準備中です",
    aboutNote: "ヴェルヴェットスキンは、ダーマペン4とコラーゲンピールを組み合わせた施術です。",
    concerns: [],
  },

  {
    slug: "lacto-peel",
    no: "06",
    en: "LACTO PEEL",
    jp: "ラクトピール",
    category: "peeling",
    heroImage:
      "/images/site/skin-lacto-peel-visual-v6.webp",
    heroImageAlt: "ラクトピールの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [PIGMENT],
  },
  {
    slug: "collagen-peel",
    no: "07",
    en: "COLLAGEN PEEL",
    jp: "コラーゲンピール",
    category: "peeling",
    heroImage:
      "/images/site/skin-collagen-peel-visual-v6.webp",
    heroImageAlt: "コラーゲンピールの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [LIFTING],
  },
  {
    slug: "mix-peel",
    no: "08",
    en: "MIX PEEL",
    jp: "MIXピール",
    category: "peeling",
    heroImage:
      "/images/site/skin-mix-peel-visual-v6.webp",
    heroImageAlt: "MIXピールの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE],
  },

  {
    slug: "plasma",
    no: "09",
    en: "PLASMA",
    jp: "プラズマ",
    category: "plasma",
    heroImage:
      "/images/site/skin-plasma-visual-v6.webp",
    heroImageAlt: "プラズマの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, LIFTING],
  },

  {
    slug: "white-photo-facial",
    no: "10",
    en: "WHITE PHOTO FACIAL",
    jp: "ホワイトフォトフェイシャル",
    category: "photo-facial",
    heroImage:
      "/images/site/skin-white-photo-facial-visual-v6.webp",
    heroImageAlt: "ホワイトフォトフェイシャルの施術イメージ",
    heroNote: "施術画像は準備中です",
    concerns: [ACNE, PIGMENT, PORES, LIFTING],
  },
];

export function getSkinTreatment(slug: string) {
  return skinTreatments.find((treatment) => treatment.slug === slug);
}